import React, { useCallback, useEffect, useState } from 'react';
import BlueprintBuilderV2, { RecipeFocusTarget } from './BlueprintBuilderV2';
import CreateProjectModal, { ProjectCreatedPayload, WorkspaceDrawerStep } from './CreateProjectModal';
import { api } from '../utils/client';
import { queryClient } from '../queryClient';
import { workspaceQueryOptions } from '../utils/workspaceApi';

export interface WorkspaceBootstrap {
  project: ProjectCreatedPayload;
  drawerStep?: WorkspaceDrawerStep;
  blueprintId?: number;
  recipeFocus?: RecipeFocusTarget | null;
  focusPlatformId?: number;
  workspaceTabRequest?: 'innovation-platform' | 'jobs-to-be-done';
  highlightedJobText?: string;
}

const SECRET_DEMO_PROJECT_MARKER = '__CATALYX_SECRET_DEMO_V1__';
const SECRET_DEMO_RECIPE_PLATFORM_NAME = 'Healthy & light but full of taste & flavour';

interface ProjectWorkflowViewProps {
  projectId: number;
  onClose: () => void;
  workspaceBootstrap?: WorkspaceBootstrap | null;
  onBootstrapConsumed?: () => void;
  onNavigateToMarketplaceProduct?: (categoryId: number, productId: number) => void;
}

const ProjectWorkflowView: React.FC<ProjectWorkflowViewProps> = ({
  projectId,
  onClose,
  workspaceBootstrap = null,
  onBootstrapConsumed,
  onNavigateToMarketplaceProduct,
}) => {
  const [blueprintId, setBlueprintId] = useState<number | undefined>(
    workspaceBootstrap?.blueprintId
  );
  const [workspaceDrawerOpen, setWorkspaceDrawerOpen] = useState(
    Boolean(workspaceBootstrap && !workspaceBootstrap.recipeFocus)
  );
  const [workspaceRefreshKey, setWorkspaceRefreshKey] = useState(0);
  const [activeBootstrap, setActiveBootstrap] = useState(workspaceBootstrap);
  const [recipeFocus, setRecipeFocus] = useState<RecipeFocusTarget | null>(
    workspaceBootstrap?.recipeFocus ?? null
  );
  const [highlightedJobId, setHighlightedJobId] = useState<string | null>(null);
  const [highlightedJobText, setHighlightedJobText] = useState<string | null>(null);
  const [workspaceTabRequest, setWorkspaceTabRequest] = useState<'innovation-platform' | 'jobs-to-be-done' | null>(null);
  const [focusPlatformId, setFocusPlatformId] = useState<number | null>(null);

  const adoptBlueprintId = useCallback((id: number | undefined) => {
    setBlueprintId(id);
    if (id) {
      void queryClient.prefetchQuery(workspaceQueryOptions(projectId, id));
    }
  }, [projectId]);

  useEffect(() => {
    if (workspaceBootstrap) {
      setActiveBootstrap(workspaceBootstrap);
      if (workspaceBootstrap.blueprintId) {
        adoptBlueprintId(workspaceBootstrap.blueprintId);
      }
      if (workspaceBootstrap.recipeFocus) {
        setRecipeFocus(workspaceBootstrap.recipeFocus);
        setWorkspaceDrawerOpen(false);
      } else {
        setWorkspaceDrawerOpen(true);
      }
      if (workspaceBootstrap.focusPlatformId) {
        setFocusPlatformId(workspaceBootstrap.focusPlatformId);
      }
      if (workspaceBootstrap.workspaceTabRequest) {
        setWorkspaceTabRequest(workspaceBootstrap.workspaceTabRequest);
      }
      if (workspaceBootstrap.highlightedJobText) {
        setHighlightedJobText(workspaceBootstrap.highlightedJobText);
      }
    }
  }, [workspaceBootstrap, adoptBlueprintId]);

  const ensureDraftBlueprint = useCallback(async (): Promise<number | undefined> => {
    try {
      const project = await api<{ markets?: string[] }>(`/api/projects/${projectId}/`);
      const created = await api<{ id: number }>('/api/blueprints/', {
        method: 'POST',
        body: JSON.stringify({
          project: projectId,
          job_to_be_done: '',
          tension: '',
          market: project.markets?.[0] || '',
          uses_new_workflow: true,
          has_been_evidenced: false,
        }),
      });
      return created.id;
    } catch {
      return undefined;
    }
  }, [projectId]);

  const resolveBlueprintId = useCallback(async () => {
    if (activeBootstrap?.blueprintId) {
      adoptBlueprintId(activeBootstrap.blueprintId);
      return;
    }
    try {
      const project = await api<{ opportunity_space?: string }>(`/api/projects/${projectId}/`);
      const response = await fetch(
        `/api/projects/${projectId}/blueprints/?uses_new_workflow=true`,
        { credentials: 'include' }
      );
      if (!response.ok) {
        const draftId = await ensureDraftBlueprint();
        adoptBlueprintId(draftId);
        return;
      }
      const data = await response.json();
      const list = Array.isArray(data) ? data : data.results || [];

      if (project.opportunity_space === SECRET_DEMO_PROJECT_MARKER) {
        const recipeBlueprint = list.find(
          (bp: { name?: string }) => bp.name?.trim() === SECRET_DEMO_RECIPE_PLATFORM_NAME
        );
        if (recipeBlueprint?.id) {
          adoptBlueprintId(recipeBlueprint.id);
          return;
        }
      }

      const withJobAndTension = list.find(
        (bp: { job_to_be_done?: string; tension?: string }) =>
          Boolean(bp.job_to_be_done?.trim() && bp.tension?.trim())
      );
      const withJob = list.find((bp: { job_to_be_done?: string }) =>
        Boolean(bp.job_to_be_done?.trim())
      );
      const preferred = withJobAndTension || withJob || list[0];
      if (preferred?.id) {
        adoptBlueprintId(preferred.id);
        return;
      }
      const draftId = await ensureDraftBlueprint();
      adoptBlueprintId(draftId);
    } catch {
      const draftId = await ensureDraftBlueprint();
      adoptBlueprintId(draftId);
    }
  }, [projectId, ensureDraftBlueprint, activeBootstrap?.blueprintId, adoptBlueprintId]);

  useEffect(() => {
    void resolveBlueprintId();
  }, [resolveBlueprintId, workspaceRefreshKey]);

  const handleWorkspaceDataChange = () => {
    setWorkspaceRefreshKey((k) => k + 1);
  };

  const handleWorkspaceClose = () => {
    setWorkspaceDrawerOpen(false);
    setHighlightedJobId(null);
    setWorkspaceTabRequest(null);
    if (activeBootstrap) {
      setActiveBootstrap(null);
      onBootstrapConsumed?.();
    }
  };

  const handleViewInnovationPlatform = () => {
    if (!blueprintId) return;
    setFocusPlatformId(blueprintId);
    setWorkspaceTabRequest('innovation-platform');
    setWorkspaceDrawerOpen(true);
  };

  const handleFocusPlatformConsumed = () => {
    setFocusPlatformId(null);
  };

  const handleViewWorkspace = async (platformId: number) => {
    adoptBlueprintId(platformId);
    try {
      // Decide builder vs filled from the first recipe card tab's phase / options
      // so a fresh tab does not inherit another tab's filled view.
      const tabs = await api<Array<{ id: number; phase?: string }>>(
        `/api/blueprints/${platformId}/recipe-card-tabs/`
      );
      const firstTab = Array.isArray(tabs) ? tabs[0] ?? null : null;
      let hasOptions = firstTab?.phase === 'options';
      if (!hasOptions && firstTab?.id) {
        const combos = await api<Array<{ id: number }>>(
          `/api/blueprints/${platformId}/recipe-combinations/?tab_id=${firstTab.id}`
        );
        const comboList = Array.isArray(combos) ? combos : [];
        const comboId = comboList[comboList.length - 1]?.id ?? null;
        if (comboId) {
          const options = await api<Array<{ id: number }>>(
            `/api/blueprints/${platformId}/recipe-attribute-options/?combination_id=${comboId}`
          );
          hasOptions = Array.isArray(options) && options.length > 0;
        }
      }
      setRecipeFocus({
        step: hasOptions ? 3 : 2,
        recipePageView: 'recipe-card',
      });
    } catch {
      setRecipeFocus({ step: 2, recipePageView: 'recipe-card' });
    }
    setWorkspaceDrawerOpen(false);
    setHighlightedJobId(null);
    setWorkspaceTabRequest(null);
  };

  const handleViewJob = (jobId: string) => {
    setHighlightedJobId(jobId);
    setWorkspaceTabRequest('jobs-to-be-done');
    setWorkspaceDrawerOpen(true);
  };

  const handleViewConcept = (platformId: number, combinationId: number, artifactId: number) => {
    adoptBlueprintId(platformId);
    setRecipeFocus({
      step: 3,
      combinationId,
      artifactId,
      recipePageView: 'product-idea',
    });
    setWorkspaceDrawerOpen(false);
    setHighlightedJobId(null);
    setWorkspaceTabRequest(null);
  };

  const handleWorkspaceTabRequestConsumed = () => {
    setWorkspaceTabRequest(null);
  };

  return (
    <div className="h-full min-h-0 flex flex-col bg-white relative">
      <BlueprintBuilderV2
        projectId={projectId}
        blueprintId={blueprintId}
        initialStep={2}
        workspaceMode
        workspaceRefreshKey={workspaceRefreshKey}
        recipeFocus={recipeFocus}
        onRecipeFocusConsumed={() => setRecipeFocus(null)}
        onClose={onClose}
        onBackToWorkspace={() => setWorkspaceDrawerOpen(true)}
        onViewInnovationPlatform={handleViewInnovationPlatform}
        onNavigateToMarketplaceProduct={onNavigateToMarketplaceProduct}
      />

      <CreateProjectModal
        mode="workspace"
        existingProjectId={projectId}
        isOpen={workspaceDrawerOpen}
        onClose={handleWorkspaceClose}
        onWorkspaceDataChange={handleWorkspaceDataChange}
        bootstrapProject={activeBootstrap?.project ?? null}
        initialDrawerStep={activeBootstrap?.drawerStep ?? 'platform-form'}
        highlightedJobId={highlightedJobId}
        highlightedJobText={highlightedJobText}
        workspaceTabRequest={workspaceTabRequest}
        onWorkspaceTabRequestConsumed={handleWorkspaceTabRequestConsumed}
        focusPlatformId={focusPlatformId}
        onFocusPlatformConsumed={handleFocusPlatformConsumed}
        onViewWorkspace={handleViewWorkspace}
        onViewJob={handleViewJob}
        onViewConcept={handleViewConcept}
        onNavigateToMarketplaceProduct={onNavigateToMarketplaceProduct}
      />
    </div>
  );
};

export default ProjectWorkflowView;
